import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { filter, Subscription } from 'rxjs';
import { AutoCompleteModel } from '../../../core/component/autocomplete/autocomplete-model';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { Dwelling } from '../../model/dwelling';
import { DwellingService } from '../../service/data/dwelling.service';
import { ComponentSendToHomeqDispatcher } from '../../service/command/component-send-to-homeq-dispatcher.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { MultipleSendToHomeQ, HQPropertyCompanyModel } from '../../model/homeq-office';

@Component({
    selector: 'app-send-to-homeq-form',
    templateUrl: './send-to-homeq-form.component.html',
    styleUrls: ['./send-to-homeq-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SendToHomeqFormComponent implements OnInit, OnDestroy {
  constructor(
    _cdf: ColumnDefinitionFactory,
    private readonly _homeQDispatcher: ComponentSendToHomeqDispatcher,
    private readonly _dwellingService: DwellingService,
    private readonly _messageService: MessageService,
    private readonly _fb: FormBuilder,
    private readonly _fcf: FormControlExFactory,
    private readonly _navigation: NavigationService
  ) {
    this.dwellingColumnDefinition = [
      _cdf.create('RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
      _cdf.create('RENTAL_OBJECT_LIST.STREET_COLUMN', 'address'),
      _cdf.create('RENTAL_OBJECT_LIST.RENTAL_OBJECT_TYPE_COLUMN', 'rentalObjectType'),
      _cdf.create('COMMON.APARTMENTNUMBER', 'apartmentNumber'),
      _cdf.createMoney('RENTAL_OBJECT_LIST.MOUNTHLY_RENT_COLUMN', 'monthlyRent'),
      _cdf.create('BUILDING_FORM.NUMBER', 'buildingNumber', ColumnVisibility.optional),
      _cdf.create('BUILDING_FORM.EDIT_HEADER', 'buildingName', ColumnVisibility.optional),
    ];
  }

  private subscription = new Subscription();
  working = false;

  dataContext: Dwelling[];
  homeQOffices: AutoCompleteModel[];
  propertyNames: string[];
  form: FormGroup;
  initialized: boolean = false;
  groupedDwellings: { [key: string]: Dwelling[] } = {};
  displayStates: { [key: string]: boolean } = {};
  assignedPropeties: HQPropertyCompanyModel[] = [];
  dwellingColumnDefinition: ColumnDefinition[];

  ngOnInit(): void {
    this.subscription.add(
      this._homeQDispatcher.data.pipe(filter(data => data.dwellings)).subscribe(data => {
        this._homeQDispatcher.dataSent();
        this.dataContext = data.dwellings.slice();

        // propertyName is not unique in DB, but propertyId is
        const properties = new Map(this.dataContext.map(dw => [dw.propertyId, dw.propertyName]));
        this.propertyNames = [...properties.values()];
        this.subscription.add(
          this._dwellingService.getHomeqOfficeDefaults([...properties.keys()]).subscribe(res => {
            this.homeQOffices = res.companies;
            this.assignedPropeties = res.propertiesCompanies;
            this.groupData();
            this.createForm();
          })
        );

        this.initialized = true;
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.subscription = null;
  }

  createForm() {
    const groupControls = {};

    for (const propName in this.groupedDwellings) {
      const officeId = this.assignedPropeties
        ?.find(x => x.propertyId === this.groupedDwellings[propName][0].propertyId)
        ?.companyId.toString();
      const office = this.homeQOffices.find(x => x.id === officeId);
      groupControls[propName] = this._fcf.createMandatory('PROPERTY.HOMEQ_OFFICE', office ?? '');
      this.displayStates[propName] = !!office;
    }

    this.form = this._fb.group(groupControls);
  }

  getSelectedData() {
    const selectedData: HQPropertyCompanyModel[] = [];

    for (const propName in this.form.value) {
      const officeId = this.form.value[propName].id;
      const dwellings = this.groupedDwellings[propName];

      if (dwellings?.length) {
        const propId = dwellings[0].propertyId;

        selectedData.push({ propertyId: propId, companyId: officeId });
      }
    }

    return selectedData;
  }

  private groupData() {
    this.groupedDwellings = this.dataContext.reduce(
      (groups, item) => {
        if (!groups[item.propertyName]) {
          groups[item.propertyName] = [];
        }

        groups[item.propertyName].push(item);
        return groups;
      },
      {} as { [key: string]: Dwelling[] }
    );
  }

  get disableSubmit() {
    return this.working || !this.initialized || !this.form.valid;
  }

  onSubmit() {
    this.working = true;
    const dataToSend = this.getSelectedData();
    const model: MultipleSendToHomeQ = { dwellingIds: this.dataContext.map(dw => dw.id), propertiesCompanies: dataToSend };
    this.subscription.add(
      this._dwellingService.sendToHomeQ(model).subscribe({
        next: () => {
          this.form.markAsPristine();
          this._messageService.showSuccess('LETTING.ADVERTISMENT.HOMEQ.DWELLINGS_SENT_SUCCESS');
          this._navigation.navigateBack();
        },
        complete: () => {
          this.working = false;
        },
      })
    );
  }

  onCancel() {
    this._navigation.navigateBack();
  }

  canDeactivate() {
    return !this.form?.dirty || this._messageService.showDialog('PENDING_CHANGES_TEXT');
  }
}
